import React, { useState, useEffect, useCallback } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

const leistungen = [
  {
    title: 'Oft über 100 € mehr im Monat',
    desc: 'Ich prüfe deine bestehenden Verträge auf Lücken, Doppelungen und schlechtes Preis-Leistungs-Verhältnis.'
  },
  {
    title: 'Sicherheit, dass im Ernstfall gezahlt wird',
    desc: 'Konkrete Empfehlung bei BU, Krankentagegeld und Risikoleben, individuell auf dich zugeschnitten.'
  },
  {
    title: 'Keine böse Überraschung im Alter',
    desc: 'Ich berechne deine Rentenlücke und schließe sie mit Fondspolice oder Basisrente.'
  },
  {
    title: 'Kein Papierkram für dich',
    desc: 'Ich übernehme die komplette Vermittlung und Antragstellung, digital und ohne Warteschleife.'
  },
  {
    title: 'Nicht allein, wenn es zählt',
    desc: 'Ich begleite dich aktiv durch den Schadensfall, nicht nur bis zur Unterschrift.'
  },
  {
    title: 'Deine Absicherung wächst mit dir',
    desc: 'Jährlicher Strategiecheck, ob noch alles zu deiner Lebenssituation passt.'
  },
];

export const LeistungenCarousel = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [visibleCards, setVisibleCards] = useState(3);
  const totalCards = leistungen.length;

  // Responsive: detect screen size
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 640) {
        setVisibleCards(1); // Mobile: 1 card
      } else if (window.innerWidth < 1024) {
        setVisibleCards(2); // Tablet: 2 cards
      } else {
        setVisibleCards(3); // Desktop: 3 cards
      }
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const maxIndex = Math.max(0, totalCards - visibleCards);

  // Reset index when visibleCards changes
  useEffect(() => {
    if (currentIndex > maxIndex) {
      setCurrentIndex(maxIndex);
    }
  }, [visibleCards, maxIndex, currentIndex]);

  const nextSlide = useCallback(() => {
    setCurrentIndex((prev) => (prev >= maxIndex ? 0 : prev + 1));
  }, [maxIndex]);

  const prevSlide = useCallback(() => {
    setCurrentIndex((prev) => (prev <= 0 ? maxIndex : prev - 1));
  }, [maxIndex]);

  const goToSlide = (index) => {
    setCurrentIndex(Math.max(0, Math.min(index, maxIndex)));
  };

  // Auto-advance every 5 seconds
  useEffect(() => {
    if (isPaused) return;
    
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev >= maxIndex ? 0 : prev + 1));
    }, 5000);

    return () => clearInterval(interval);
  }, [isPaused, maxIndex]);

  // Calculate card width based on visible cards
  const getCardWidth = () => {
    if (visibleCards === 1) return '100%';
    if (visibleCards === 2) return 'calc((100% - 1rem) / 2)';
    return 'calc((100% - 2rem) / 3)';
  };

  // Calculate transform based on visible cards
  const getTransform = () => {
    if (visibleCards === 1) {
      return `translateX(calc(-${currentIndex} * (100% + 1rem)))`;
    }
    if (visibleCards === 2) {
      return `translateX(calc(-${currentIndex} * (calc((100% - 1rem) / 2) + 1rem)))`;
    }
    return `translateX(calc(-${currentIndex} * (calc((100% - 2rem) / 3) + 1rem)))`;
  };

  return (
    <div 
      className="relative"
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
      onTouchStart={() => setIsPaused(true)}
      onTouchEnd={() => setIsPaused(false)}
    >
      {/* Navigation Arrows */}
      <button
        onClick={prevSlide}
        className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-2 sm:-translate-x-4 lg:-translate-x-6 z-10 w-8 h-8 sm:w-10 sm:h-10 lg:w-12 lg:h-12 bg-white rounded-full shadow-lg border border-black/5 flex items-center justify-center hover:bg-ice transition-colors"
        aria-label="Vorherige Leistung"
      >
        <ChevronLeft className="w-4 h-4 sm:w-5 sm:h-5 lg:w-6 lg:h-6 text-navy" />
      </button>
      
      <button
        onClick={nextSlide}
        className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-2 sm:translate-x-4 lg:translate-x-6 z-10 w-8 h-8 sm:w-10 sm:h-10 lg:w-12 lg:h-12 bg-white rounded-full shadow-lg border border-black/5 flex items-center justify-center hover:bg-ice transition-colors"
        aria-label="Nächste Leistung"
      >
        <ChevronRight className="w-4 h-4 sm:w-5 sm:h-5 lg:w-6 lg:h-6 text-navy" />
      </button>

      {/* Cards Container */}
      <div className="overflow-hidden mx-6 sm:mx-8 lg:mx-10">
        <div 
          className="flex transition-transform duration-500 ease-out"
          style={{ 
            gap: '1rem',
            transform: getTransform(),
          }}
        >
          {leistungen.map((card, i) => (
            <div
              key={i}
              className="flex-shrink-0 bg-ice rounded-t-[2rem] rounded-b-xl p-5 sm:p-6 lg:p-8 min-h-[200px] sm:min-h-[220px] flex flex-col"
              style={{ width: getCardWidth() }}
            >
              <h3 className="font-playfair text-base sm:text-lg lg:text-xl font-bold text-navy mb-2 sm:mb-3">
                {card.title}
              </h3>
              <p className="text-sm text-charcoal leading-relaxed flex-grow">
                {card.desc}
              </p>
            </div>
          ))}
        </div>
      </div>

      {/* Dots Indicator */}
      <div className="flex justify-center gap-2 mt-4 sm:mt-6">
        {Array.from({ length: maxIndex + 1 }).map((_, i) => (
          <button
            key={i}
            onClick={() => goToSlide(i)}
            className={`h-2 rounded-full transition-all duration-300 ${
              currentIndex === i
                ? 'bg-navy w-6' 
                : 'bg-navy/20 hover:bg-navy/40 w-2'
            }`}
            aria-label={`Gehe zu Position ${i + 1}`}
          />
        ))}
      </div>
    </div>
  );
};

export default LeistungenCarousel;
